import { fetcher, useApplicantAPI, useApplicantSubmit } from "@certn/api";
import useSWR, { useSWRConfig } from "swr";

import {
  informationRequestPath,
  informationRequestsPath,
  itemCannotProvidePath,
  itemSubmitPath,
  itemTextResponsePath,
} from "./constants";
import { CaseOutstandingRequests, InformationRequestSummary } from "./types";
import { isOutstandingRequest } from "./utils";

const OUTSTANDING_REQUESTS_KEY = "outstanding-information-requests";

/** Outstanding and complete requests on the case, oldest first. Doesn't mark them viewed. */
export function useInformationRequests(caseId: string | undefined) {
  return useApplicantAPI(caseId ? informationRequestsPath(caseId) : null);
}

/** Opening a request records that the applicant has seen it. */
export function useInformationRequest(caseId: string, requestId: string) {
  return useApplicantAPI(informationRequestPath(caseId, requestId));
}

async function fetchOutstandingRequests(
  caseId: string
): Promise<CaseOutstandingRequests> {
  try {
    const requests = await fetcher.request<InformationRequestSummary[]>(
      informationRequestsPath(caseId)
    );
    return { caseId, requests: requests.filter(isOutstandingRequest) };
  } catch {
    return { caseId, requests: [] };
  }
}

/**
 * Lists each case's requests to find the outstanding ones. A case whose list
 * fails counts as having none, so one bad case doesn't hide the others.
 */
export function useOutstandingInformationRequests(caseIds: string[]) {
  return useSWR(
    caseIds.length ? [OUTSTANDING_REQUESTS_KEY, ...caseIds] : null,
    () => Promise.all(caseIds.map(fetchOutstandingRequests)),
    { revalidateOnFocus: false }
  );
}

export function useInformationRequestsCache(caseId: string) {
  const { mutate } = useSWRConfig();
  const isCaseKey = (key: unknown) =>
    typeof key === "string" && key.startsWith(informationRequestsPath(caseId));
  const isOutstandingKey = (key: unknown) =>
    Array.isArray(key) && key[0] === OUTSTANDING_REQUESTS_KEY;

  return {
    refresh: () => mutate(isCaseKey),
    refreshOutstanding: () => mutate(isOutstandingKey),
    clear: () =>
      Promise.all([
        mutate(isCaseKey, undefined, { revalidate: false }),
        mutate(isOutstandingKey, undefined),
      ]),
  };
}

const SAVE_OPTIONS = { throwErrors: true, showToastOnError: true };

/** Leaves reporting a failure to the caller. */
export function useSaveTextResponse(caseId: string, itemId: string) {
  return useApplicantSubmit(itemTextResponsePath(caseId, itemId), {
    throwErrors: true,
  }).submit;
}

export function useSaveCannotProvide(caseId: string, itemId: string) {
  return useApplicantSubmit(itemCannotProvidePath(caseId, itemId), SAVE_OPTIONS)
    .submit;
}

export function useSubmitItem(caseId: string, itemId: string) {
  return useApplicantSubmit(itemSubmitPath(caseId, itemId), {
    throwErrors: true,
  }).submit;
}
